/**
 * Live input: everything arrives as records on the Jetstream firehose — the
 * firehose IS the multiplayer transport. No pairing protocol, no sockets to
 * the phone: a hunter claims the lobby with a `game` record, zapping is a
 * `zap` record referencing it. The screen is the single authoritative simulation and
 * judges by ARRIVAL time, never by the record's own createdAt.
 */

export const ZAP_COLLECTION = 'games.firehose.dh.zap';
export const GAME_COLLECTION = 'games.firehose.dh.game';

// Sri's own taps first (first-party, and jetstream2.us-east provably
// dropped a join-era dh event on 2026-08-17 — it is exiled from this list).
const JETSTREAMS = [
  'wss://nyc.firehose.stream/tap',
  'wss://jet.firehose.stream/tap',
  'wss://sfo.firehose.stream/tap',
  'wss://jetstream1.us-east.bsky.network/subscribe',
  'wss://jetstream2.us-west.bsky.network/subscribe'
];

/**
 * connectGame — subscribes to zap + game records. Reconnects forever with a
 * short backoff, rotating endpoints. Returns a disposer.
 */
export function connectGame({onZap, onGame}) {
  let ws = null;
  let endpointIndex = 0;
  let attempt = 0;
  let disposed = false;
  let watchdog = null;
  // Our collections are SPARSE — long silences are normal, so the watchdog
  // may rotate healthy sockets. That's fine only because rotation is
  // LOSSLESS: reconnects resume from the last time_us cursor (the endpoint
  // replays the gap) and the dedupe window absorbs any overlap.
  let cursorUs = 0;
  const seen = new Set(); // did/collection/rkey — insertion-ordered, trimmed oldest-first
  // handlers run strictly in ARRIVAL order: a zap must not be judged while
  // the game record that admits its hunter is still mid-processing
  let pipeline = Promise.resolve();

  // capped exponential backoff with jitter — endpoints rotate each attempt
  const scheduleReconnect = () => {
    endpointIndex++;
    attempt++;
    const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5))
      * (0.5 + Math.random() * 0.5);
    setTimeout(open, delay);
  };

  const open = () => {
    if (disposed) {
      return;
    }
    const base = JETSTREAMS[endpointIndex % JETSTREAMS.length];
    let socket;
    try {
      socket = new WebSocket(
        `${base}?wantedCollections=${ZAP_COLLECTION}&wantedCollections=${GAME_COLLECTION}` +
        // rewind 2 s: cursor boundary semantics vary and same-µs events
        // exist — the dedupe window swallows whatever replays
        (cursorUs ? `&cursor=${Math.max(0, cursorUs - 2000000)}` : ''));
    } catch (e) {
      scheduleReconnect(); // a throwing constructor must not end the loop
      return;
    }
    ws = socket;
    // jetstream emits continuously — a minute of total silence is a zombie
    // socket (open but deaf); close it so onclose rotates endpoints
    let lastFrame = Date.now();
    const socketWatchdog = setInterval(() => {
      if (Date.now() - lastFrame > 60000) {
        socket.close();
      }
    }, 10000);
    clearInterval(watchdog);
    watchdog = socketWatchdog; // disposer clears whichever is current
    socket.onmessage = (event) => {
      lastFrame = Date.now();
      attempt = 0; // a live frame proves the endpoint — reset the backoff
      try {
        const msg = JSON.parse(event.data);
        if (msg.time_us && msg.time_us > cursorUs) {
          cursorUs = msg.time_us;
        }
        if (msg.kind !== 'commit' || !msg.commit || msg.commit.operation !== 'create' || !msg.commit.record) {
          return;
        }
        const dedupeKey = `${msg.did}/${msg.commit.collection}/${msg.commit.rkey}`;
        if (seen.has(dedupeKey)) {
          return; // cursor-overlap replay after a rotation
        }
        seen.add(dedupeKey);
        if (seen.size > 1500) {
          // far wider than the 2 s replay overlap at any realistic event
          // rate — keys stay alive through the whole replay window
          seen.delete(seen.values().next().value); // trim oldest
        }
        // arrivedAt is stamped at ENQUEUE — pipeline delay must not shift
        // the judge's arrival-time window
        const evt = {did: msg.did, rkey: msg.commit.rkey, record: msg.commit.record,
          arrivedAt: Date.now()};
        if (msg.commit.collection === ZAP_COLLECTION && onZap) {
          pipeline = pipeline.then(() => onZap(evt))
            .catch((err) => console.log('[duckhunt] zap handler failed', err));
        } else if (msg.commit.collection === GAME_COLLECTION && onGame) {
          pipeline = pipeline.then(() => onGame(evt))
            .catch((err) => console.log('[duckhunt] game handler failed', err));
        }
      } catch (e) {
        // malformed frame — the firehose is best-effort, skip it
      }
    };
    socket.onclose = () => {
      clearInterval(socketWatchdog); // its OWN watchdog — never the successor's
      if (!disposed && ws === socket) { // a superseded socket must not reconnect
        scheduleReconnect();
      }
    };
    socket.onerror = () => socket.close();
  };

  open();
  return () => {
    disposed = true;
    clearInterval(watchdog);
    if (ws) {
      ws.close();
    }
  };
}

const profileCache = new Map();

/** did → {handle, avatar} via the public AppView; degrades to a did stub. */
export function resolveProfile(did) {
  if (!profileCache.has(did)) {
    profileCache.set(did, (async () => {
      try {
        const res = await fetch(
          `https://public.api.bsky.app/xrpc/app.bsky.actor.getProfile?actor=${encodeURIComponent(did)}`,
          {signal: AbortSignal.timeout(4000)}); // a stalled fetch must not delay the judge
        if (!res.ok) {
          throw new Error(String(res.status));
        }
        const profile = await res.json();
        if (!profile.handle) {
          throw new Error('profile without handle'); // fall to the did stub
        }
        return {handle: profile.handle, avatar: profile.avatar || null};
      } catch (e) {
        profileCache.delete(did); // allow a retry later
        return {handle: String(did || '').replace('did:plc:', '').slice(0, 10), avatar: null};
      }
    })());
  }
  return profileCache.get(did);
}
